import React from 'react';
import { Collapse, Form, Input, InputNumber, Select, Typography } from 'antd';
import type { ProductSetupSectionProps, DefaultSizeGridSectionProps } from '../../interfaces';

const { Text, Title } = Typography;



export const ProductSetupSection: React.FC<ProductSetupSectionProps> = ({ types }) => (
  <div className="admin-product-create__section">
    <Title level={5}>Product Setup</Title>
    <div className="admin-product-create__grid">
      <Form.Item name="productTypeId" label="Product Type" rules={[{ required: true, message: 'Select product type.' }]}>
        <Select placeholder="Select product type" options={types.map((type) => ({ value: type.id, label: type.name }))} />
      </Form.Item>
      <Form.Item name="gender" label="Gender" rules={[{ required: true, message: 'Select gender.' }]}>
        <Select options={[{ value: 'unisex', label: 'Unisex' }, { value: 'womens', label: "Women's" }, { value: 'mens', label: "Men's" }]} />
      </Form.Item>
    </div>
  </div>
);

export const BasicInformationSection: React.FC = () => (
  <div className="admin-product-create__section">
    <Title level={5}>Basic Information</Title>
    <div className="admin-product-create__grid">
      <Form.Item name="title" label="Title" rules={[{ required: true, message: 'Enter product title.' }]}>
        <Input placeholder="Classic Engagement Ring" />
      </Form.Item>
      <Form.Item name="price" label="Base Price" rules={[{ required: true, message: 'Enter base price.' }, { type: 'number', min: 1, message: 'Base price must be greater than 0.' }]}>
        <InputNumber min={1} style={{ width: '100%' }} />
      </Form.Item>
    </div>
    <Form.Item name="description" label="Description" rules={[{ required: true, message: 'Add a product description.' }]}>
      <Input.TextArea rows={3} placeholder="Short product description" />
    </Form.Item>
  </div>
);

export const DefaultSizeGridSection: React.FC<DefaultSizeGridSectionProps> = ({
  filteredSizes,
  filteredSizeIds,
  selectedTypeRequiresSizes,
}) => (
  <div className="admin-product-create__section">
    <Title level={5}>Default Size Grid</Title>
    <Form.Item
      name="sizeIds"
      label="Sizes used as the default for new materials"
      rules={[{
        validator: async (_, value: string[] = []) => {
          if (selectedTypeRequiresSizes && !value.length) {
            return Promise.reject(new Error('Select at least one size for this product type.'));
          }

          if (!selectedTypeRequiresSizes && value.length) {
            return Promise.reject(new Error('This product type does not use selectable sizes.'));
          }

          const invalidSizeIds = value.filter((id) => !filteredSizeIds.includes(id));
          return invalidSizeIds.length
            ? Promise.reject(new Error('Selected sizes do not match product type and gender.'))
            : Promise.resolve();
        },
      }]}
    >
      <Select
        mode="multiple"
        allowClear
        disabled={!filteredSizes.length}
        placeholder={filteredSizes.length ? 'Select default sizes for materials' : 'No sizes for selected type/gender'}
        options={filteredSizes.map((size) => ({ value: size.id, label: `${size.label} (${size.value})` }))}
      />
    </Form.Item>
  </div>
);

export const ProductDetailsCollapse: React.FC = () => (
  <Collapse
    className="admin-product-create__details-collapse"
    items={[{
      key: 'details',
      label: 'Product Details · Recommended',
      children: (
        <>
          <Text className="admin-product-create__details-recommendation">
            Recommended for richer product pages. These fields can be completed later.
          </Text>
          <div className="admin-product-create__grid">
            <Form.Item name="detailsStyle" label="Style">
              <Input placeholder="Solitaire" />
            </Form.Item>
            <Form.Item name="detailsMetal" label="Metal"><Input placeholder="14K Gold" /></Form.Item>
            <Form.Item name="detailsWidth" label="Average Width"><Input placeholder="2.1 mm" /></Form.Item>
            <Form.Item name="detailsLength" label="Length"><Input placeholder="18 in" /></Form.Item>
            <Form.Item name="detailsSetting" label="Setting"><Input placeholder="Prong" /></Form.Item>
            <Form.Item name="detailsBacking" label="Backing"><Input placeholder="Push back" /></Form.Item>
          </div>
        </>
      ),
    }]}
  />
);
